/**
 * Mirror of `useFileOpenFromAgent` for the Team of Agents pane —
 * subscribes to `session.tool` events and reacts to the two
 * concierge UI-command tools shipped in Part E (commit `b63e6094`):
 *
 *   - `lx_show_team_pane` — no args; the concierge pops the right
 *     sidebar open in the lawyer's browser.
 *   - `lx_focus_mission` — `{mission_id}`; the concierge expands the
 *     sidebar AND scroll-into-views the row with a brief highlight
 *     pulse (same mechanic as the §I.4 self-focus click).
 *
 * Both tools are no-op work on the runtime side — they exist purely
 * to ride the gateway's automatic `session.tool` broadcast (see
 * `useFileOpenFromAgent` header for the full pattern). This hook
 * picks the broadcast up + mutates `useUIStore` (pane open) +
 * `useMissionsStore` (focus pulse).
 *
 * **Mount site:** App.tsx chrome wrapper — sibling of
 * `ActivityBar` + `SidePanel`. Must mount globally so the concierge
 * nudge works from any route (home / chat / case / settings),
 * matching plan §I.1's single-right-side-surface decision and §B.1
 * goal #1 (cross-mode persistence). Do NOT mount inside
 * `CaseWorkspaceLayout` like `useFileOpenFromAgent` does — that
 * would silently drop the broadcast on non-case routes.
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md §H.6 + §J.S6.
 */
import { useEventStream } from "@/hooks/useEventStream";
import { useUIStore } from "@/stores/useUIStore";
import { useMissionsStore } from "@/stores/useMissionsStore";

/** Mirror of the real `AgentEventPayload` shape.
 *
 *  Wire-level reality: openclaw's `session.tool` EventFrame ships the
 *  tool name as `data.name` + the input as `data.args` (see
 *  `lexruntime/openclaw/src/gateway/server-chat.agent-events.test.ts`
 *  fixtures, e.g. `data: { phase: "start", name: "read", toolCallId,
 *  args }`). The legacy `toolName`/`toolInput` shape is what message-
 *  content blocks get rekeyed into (see `content-blocks.ts:218`), NOT
 *  what the live broadcast carries. Five sibling listeners
 *  (`useFileOpenFromAgent`, `useSidecarUpdatedStream`,
 *  `useBrowserActivityFromAgent`, `usePlatformOpListener`,
 *  `AgentBrowserLayout`) read the legacy fields and miss the broadcast
 *  silently — flagged for a follow-up sweep. This hook accepts both
 *  via `??` so it works against the wire shape AND any future
 *  rekey middleware that may be added. */
export interface AgentEventPayloadRuntime {
  runId?: string;
  seq?: number;
  stream?: string;
  ts?: number;
  sessionKey?: string;
  data?: {
    phase?: string;
    /** Wire field — what the broadcast actually emits. */
    name?: string;
    /** Wire field — what the broadcast actually emits. */
    args?: Record<string, unknown>;
    /** Legacy/rekeyed field — accepted for forward compatibility. */
    toolName?: string;
    /** Legacy/rekeyed field — accepted for forward compatibility. */
    toolInput?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

/** Dependencies for the pure handler. Production wires straight to
 *  the store setters; tests inject vi.fn stubs. */
export interface TeamPaneCommandHandlerDeps {
  setRightSidebarExpanded: (expanded: boolean) => void;
  focusRow: (rowId: string) => void;
}

/** Pure handler for a single `session.tool` broadcast. Extracted
 *  from the React hook below so the dispatch logic can be unit-
 *  tested without `renderHook` (per lexide convention:
 *  `useFeedback.test.ts` header). The React hook is then a thin
 *  `useEventStream` wrapper. */
export function handleTeamPaneCommand(
  payload: unknown,
  deps: TeamPaneCommandHandlerDeps,
): void {
  const typed = payload as AgentEventPayloadRuntime;
  // Fire only on the start-phase event (mirrors
  // useFileOpenFromAgent). The gateway also emits an "end" phase
  // that we ignore to avoid double-fires.
  if (typed.data?.phase !== "start") return;

  const tool = typed.data?.name ?? typed.data?.toolName;
  if (tool === "lx_show_team_pane") {
    deps.setRightSidebarExpanded(true);
    return;
  }
  if (tool === "lx_focus_mission") {
    const input = typed.data?.args ?? typed.data?.toolInput;
    const missionId = input?.mission_id;
    if (typeof missionId !== "string" || missionId.length === 0) return;
    deps.setRightSidebarExpanded(true);
    deps.focusRow(`mission:${missionId}`);
  }
}

export function useTeamPaneCommandsFromAgent(): void {
  const setRightSidebarExpanded = useUIStore((s) => s.setRightSidebarExpanded);
  const focusRow = useMissionsStore((s) => s.focusRow);

  useEventStream("session.tool", (payload) => {
    handleTeamPaneCommand(payload, { setRightSidebarExpanded, focusRow });
  });
}
